import { CustomState } from "@bevco/Store/Reducers";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import InvoiceDetailsBillingAddress, {
    InvoiceDetailsBillingAddressStyles,
} from "@insite/content-library/Widgets/InvoiceDetails/InvoiceDetailsBillingAddress";
import InvoiceDetailsShippingAddress, {
    InvoiceDetailsShippingAddressStyles,
} from "@insite/content-library/Widgets/InvoiceDetails/InvoiceDetailsShippingAddress";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import * as React from "react";
import { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => ({
    language: state.context.session.language,

    /* Verndale Custom Feature - New variables */
    invoiceTypeList: state.customSystemList["InvoiceTypes"] ?? [],
});

type Props = ReturnType<typeof mapStateToProps>;

export interface InvoiceDetailsInformationStyles {
    invoiceInformationGridContainer?: GridContainerProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    informationGridItem?: GridItemProps;
    invoiceInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    shippingMethodGridItem?: GridItemProps;
    invoiceDateGridItem?: GridItemProps;
    termsGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    vatNumberGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    invoiceNotesGridItem?: GridItemProps;
    billingAddressStyle?: InvoiceDetailsBillingAddressStyles;
    shippingAddressStyle?: InvoiceDetailsShippingAddressStyles;

    /* Verndale Custom Feature */
    addressGridContainer?: GridContainerProps;
}

export const informationStyles: InvoiceDetailsInformationStyles = {
    invoiceInformationGridContainer: {
        css: css`
            padding: 5px;
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h6",
            as: "h2",
            css: css`
                @media print {
                    font-size: 12px;
                }
                margin-bottom: 5px;
            `,
        },
    },
    informationGridItem: {
        width: 12,
    },
    invoiceInformationGridItems: {
        css: css`
            @media print {
                padding: 5px !important;
            }
        `,
    },
    statusGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    invoiceDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    termsGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    poNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    salespersonGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    shippingMethodGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    dueDateGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    vatNumberGridItem: {
        width: [6, 6, 6, 3, 3],
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    invoiceNotesGridItem: {
        width: 12,
    },
};

const styles = informationStyles;

const InvoiceDetailsInformation = ({ language, invoiceTypeList }: Props) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    if (!invoice) {
        return null;
    }

    /* Verndale Custom Feature - Get order number from the first invoice line */
    const orderNumber = (invoice.invoiceLines ?? []).length > 0 ? invoice.invoiceLines![0].erpOrderNumber : "";

    /* Verndale Custom Feature - Get label from invoice type */
    const showInvoiceTypeLabel = () => {
        const typeFromList = invoiceTypeList.find(item => item.name === invoice.invoiceType);
        if (typeFromList) {
            return typeFromList.description ?? "";
        }
        return invoice.invoiceType;
    };

    const isInvoice = invoice.invoiceType.toUpperCase() === "M";
    const isInvoiceCancelation = invoice.invoiceType.toUpperCase() === "N";
    const isCreditMemo = invoice.invoiceType.toUpperCase() === "O";

    return (
        <GridContainer {...styles.invoiceInformationGridContainer} data-test-selector="invoiceDetail_information">
            <GridItem {...styles.informationGridItem}>
                <GridContainer {...styles.informationGridContainer}>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceDateGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={`${translate("{0} Date", showInvoiceTypeLabel())}: `}
                            text={getLocalizedDateTime({
                                dateTime: new Date(invoice.invoiceDate),
                                language,
                            })}
                        />
                    </GridItem>

                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.statusGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={`${translate("Status")}: `}
                            text={invoice.status}
                        />
                    </GridItem>

                    {/* IEIXOP-6507 - Verndale Custom Feature - Hide unused property */}
                    {/* {isInvoice && (
                        <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.dueDateGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={`${translate("Due Date")}:`}
                                text={getLocalizedDateTime({
                                    dateTime: new Date(invoice.dueDate),
                                    language,
                                })}
                            />
                        </GridItem>
                    )} */}

                    {isInvoice && (
                        <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.termsGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={`${translate("Terms")}:`}
                                text={invoice.terms}
                            />
                        </GridItem>
                    )}

                    {invoice.customerPO && (
                        <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.poNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={`${translate("Customer P.O.")}:`}
                                text={invoice.customerPO}
                            />
                        </GridItem>
                    )}

                    {orderNumber && isInvoice && (
                        <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.poNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                /* Verndale Custom Feature - Change translation */
                                heading={`${translate("Order #")}:`}
                                text={orderNumber}
                            />
                        </GridItem>
                    )}

                    {invoice.shipCode && (
                        <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.shippingMethodGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Shipping Method")}
                                text={invoice.shipViaDescription || invoice.shipCode}
                            />
                        </GridItem>
                    )}

                    {/* Verndale Custom Feature - Hide unused property */}
                    {/* <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.salespersonGridItem)}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Salesperson")}
                            text={invoice.salesperson}
                        />
                    </GridItem> */}

                    {invoice.customerVatNumber && (
                        <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.vatNumberGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("VAT Number")}
                                text={invoice.customerVatNumber}
                            />
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>

            {/* Verndale Custom Feature - Organize display for elements */}
            <GridItem {...styles.informationGridItem}>
                <GridContainer {...styles.addressGridContainer}>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.billingAddressGridItem)}>
                        <InvoiceDetailsBillingAddress invoice={invoice} extendedStyles={styles.billingAddressStyle} />
                    </GridItem>
                    <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.shippingAddressGridItem)}>
                        <InvoiceDetailsShippingAddress invoice={invoice} extendedStyles={styles.shippingAddressStyle} />
                    </GridItem>
                    {invoice.notes && (
                        <GridItem {...mergeToNew(styles.invoiceInformationGridItems, styles.invoiceNotesGridItem)}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Invoice Notes")}
                                text={invoice.notes}
                            />
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(InvoiceDetailsInformation as any) as any,
    definition: {
        group: "Invoice Details",
        displayName: "Information",
        allowedContexts: ["InvoiceDetailsPage"],
    },
};

export default widgetModule;
